import { getUnitMeasureLabel, normalizeOldUnitSize } from '../constants/unitMeasures';

/**
 * StoreProduct - Simplified product model for store ordering
 * Unlike MerchantProduct, this is not tied to a specific merchant/supplier.
 * Used in the simplified employee ordering flow.
 */
export class StoreProduct {
  constructor({
    id,
    name,
    orderUnit,          // What you order in (box, case, bag)
    pricingUnit,        // What you're billed by (lb, kg, each)
    pricePerPricingUnit,
    estimatedConversion, // Optional: estimated pricing units per order unit (e.g., 40 lbs per box)
    unitMeasure,        // Optional: standardized unit measure (e.g., "lb", "oz", "kg")
    active = true,
    createdAt,
    lastUpdatedAt,
  }) {
    this.id = id;
    this.name = name;
    this.orderUnit = orderUnit || 'each';
    this.pricingUnit = pricingUnit || 'each';
    this.pricePerPricingUnit = pricePerPricingUnit ?? 0;
    this.estimatedConversion = estimatedConversion || null;
    this.unitMeasure = unitMeasure || null;
    this.active = active;
    this.createdAt = createdAt;
    this.lastUpdatedAt = lastUpdatedAt || null;
  }

  // Check if this product has different order vs pricing units
  hasDualUnits() {
    return this.orderUnit !== this.pricingUnit;
  }

  // Get estimated cost for a given order quantity
  getEstimatedCost(orderQuantity) {
    if (this.hasDualUnits() && this.estimatedConversion) {
      return orderQuantity * this.estimatedConversion * this.pricePerPricingUnit;
    }
    // Same units: price x quantity
    return orderQuantity * this.pricePerPricingUnit;
  }

  // Get estimated weight/amount for a given order quantity
  getEstimatedAmount(orderQuantity) {
    if (this.hasDualUnits() && this.estimatedConversion) {
      return orderQuantity * this.estimatedConversion;
    }
    return orderQuantity;
  }

  // Format price display
  getPriceDisplay() {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(this.pricePerPricingUnit);

    if (this.hasDualUnits()) {
      return `${formatted}/${this.pricingUnit}`;
    }
    return `${formatted}/${this.orderUnit}`;
  }

  // Format conversion info display (e.g., "~40 lbs")
  getConversionDisplay() {
    if (this.hasDualUnits() && this.estimatedConversion) {
      return `~${this.estimatedConversion} ${this.pricingUnit}`;
    }
    return null;
  }

  // Format order unit with measure (e.g., "box (Pound)" or just "box")
  getUnitDisplay() {
    if (this.unitMeasure) {
      const label = getUnitMeasureLabel(this.unitMeasure);
      return `${this.orderUnit} (${label})`;
    }
    return this.orderUnit;
  }

  toJson() {
    return {
      name: this.name,
      orderUnit: this.orderUnit,
      pricingUnit: this.pricingUnit,
      pricePerPricingUnit: this.pricePerPricingUnit,
      estimatedConversion: this.estimatedConversion,
      unitMeasure: this.unitMeasure,
      active: this.active,
      createdAt: this.createdAt,
      lastUpdatedAt: this.lastUpdatedAt,
    };
  }

  static fromFirebase(doc) {
    const data = doc.data();

    // Backwards compatibility: handle old unitSize data
    let unitMeasure = data.unitMeasure || null;
    if (!unitMeasure && data.unitSize) {
      unitMeasure = normalizeOldUnitSize(data.unitSize);
    }

    return new StoreProduct({
      id: doc.id,
      name: data.name,
      orderUnit: data.orderUnit,
      pricingUnit: data.pricingUnit,
      pricePerPricingUnit: data.pricePerPricingUnit,
      estimatedConversion: data.estimatedConversion,
      unitMeasure,
      active: data.active !== false,
      createdAt: data.createdAt,
      lastUpdatedAt: data.lastUpdatedAt,
    });
  }
}
